// src/api.js

const API_BASE = "/api";

// In-memory cache: key -> { exp: number, data: any }
const _cache = new Map();
// In-flight de-dupe: key -> Promise
const _inflight = new Map();

const DEFAULT_TTL_MS = 30_000; // 30s

function now() {
  return Date.now();
}

function makeKey(url, options) {
  const method = (options?.method || "GET").toUpperCase();
  return `${method}:${url}`; // include querystring as-is
}

function isCacheable(options, url) {
  const method = (options?.method || "GET").toUpperCase();
  if (method !== "GET") return false;
  if (options?.body != null) return false;
  if (options?.cache === "no-store") return false;

  // Never cache auth/session endpoints
  if (url.startsWith("/auth/")) return false;

  // You *can* cache /me/* (you already do) but keep TTL short + bust on 401
  return true;
}

function shouldSendJsonContentType(body, headers) {
  // if caller already set content-type, respect it
  const hasCT =
    Object.keys(headers || {}).some((h) => h.toLowerCase() === "content-type");
  if (hasCT) return false;

  // no body -> don't set
  if (body === undefined || body === null) return false;

  // FormData / Blob / ArrayBuffer -> don't set JSON CT
  if (typeof FormData !== "undefined" && body instanceof FormData) return false;
  if (typeof Blob !== "undefined" && body instanceof Blob) return false;
  if (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) return false;

  // string could be JSON or something else; if it's a string, assume caller knows
  if (typeof body === "string") return false;

  // plain object/array -> yes JSON
  return true;
}

async function doFetch(url, options = {}) {
  const method = (options.method || "GET").toUpperCase();

  const headers = {
    ...(options.headers || {}),
  };

  // Only set JSON content-type when we are actually sending JSON
  if (shouldSendJsonContentType(options.body, headers)) {
    headers["Content-Type"] = "application/json";
  }

  const fetchOpts = {
    method,
    headers,
    // ✅ critical for cookie auth (your __Host-olym_uid)
    credentials: options.credentials || "include",
    // allow aborts
    signal: options.signal,
  };

  if (options.body !== undefined) {
    if (
      typeof FormData !== "undefined" &&
      options.body instanceof FormData
    ) {
      fetchOpts.body = options.body;
    } else if (typeof options.body === "string") {
      fetchOpts.body = options.body;
    } else {
      fetchOpts.body = JSON.stringify(options.body);
    }
  }

  const res = await fetch(`${API_BASE}${url}`, fetchOpts);

  // No content
  if (res.status === 204) return null;

  // Try JSON, fallback to text
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg =
      data?.error ||
      data?.message ||
      (typeof data?.raw === "string" ? data.raw.slice(0, 200) : null) ||
      `Request failed (${res.status})`;

    const err = new Error(msg);
    err.status = res.status;
    err.data = data;

    // ✅ IMPORTANT: if unauthorized, nuke cached GETs
    // This prevents “stuck” UI when cookie changes / expires.
    if (res.status === 401) {
      api.clearCache();
    }

    throw err;
  }

  return data;
}

export async function api(url, options = {}) {
  const cacheable = isCacheable(options, url);
  const key = cacheable ? makeKey(url, options) : null;

  const ttlMs =
    typeof options.cacheTtlMs === "number" ? options.cacheTtlMs : DEFAULT_TTL_MS;

  // -----------------------
  // GET caching path
  // -----------------------
  if (cacheable && key) {
    const hit = _cache.get(key);
    if (hit && hit.exp > now()) return hit.data;

    const inflight = _inflight.get(key);
    if (inflight) return inflight;

    const p = (async () => {
      try {
        const data = await doFetch(url, options);
        _cache.set(key, { exp: now() + ttlMs, data });
        return data;
      } finally {
        _inflight.delete(key);
      }
    })();

    _inflight.set(key, p);
    return p;
  }

  // -----------------------
  // Non-GET or no-cache path
  // -----------------------
  const data = await doFetch(url, options);

  // Optional invalidation controls
  if (options.invalidateAll) {
    api.clearCache();
  }

  if (Array.isArray(options.invalidatePrefixes)) {
    for (const p of options.invalidatePrefixes) {
      api.invalidatePrefix(p);
    }
  }

  return data;
}

// ---- Cache helpers ----
api.clearCache = () => {
  _cache.clear();
  _inflight.clear();
};

api.invalidatePrefix = (prefix) => {
  for (const k of _cache.keys()) {
    // k looks like "GET:/me/workout-done?ymd=2025-12-14"
    if (k.startsWith("GET:") && k.includes(prefix)) {
      _cache.delete(k);
    }
  }
};